import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, LayoutDashboard, ClipboardCheck, FolderArchive, CalendarClock, BarChart3, Check, Sparkles } from 'lucide-react';
import { uiScale } from '../lib/uiScale';

interface UpgradeModalProps {
    open: boolean;
    onClose: () => void;
    source?: string;
}

interface FeatureSlide {
    id: string;
    title: string;
    description: string;
    bullets: string[];
    icon: React.ReactNode;
    gradient: string;
    image: string; // Added image property
}

const features: FeatureSlide[] = [
    {
        id: 'portfolio',
        title: 'Portfolio Dashboard',
        description: 'See all your investments in one place.',
        bullets: ['Track total exposure & equity gains', 'Monitor timelines at a glance'],
        icon: <LayoutDashboard size={24} />,
        gradient: 'from-blue-500 to-indigo-600',
        image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800' // Dashboard/Analytics
    },
    {
        id: 'tracking',
        title: 'Investment Tracking',
        description: 'Never miss a milestone.',
        bullets: ['Log payments & monitor progress', 'Track from reservation to handover'],
        icon: <ClipboardCheck size={24} />,
        gradient: 'from-emerald-500 to-teal-600',
        image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&q=80&w=800' // Tracking/Checklist
    },
    {
        id: 'vault',
        title: 'Document Vault',
        description: 'All your files, organized.',
        bullets: ['Store receipts, contracts, floorplans', 'Access documents per investment'],
        icon: <FolderArchive size={24} />,
        gradient: 'from-amber-500 to-orange-600',
        image: 'https://images.unsplash.com/photo-1618044733300-9472054094ee?auto=format&fit=crop&q=80&w=800' // Documents/Files
    },
    {
        id: 'timeline',
        title: 'Construction Timeline',
        description: 'Follow every update.',
        bullets: ['Progress updates & status changes', 'Completion targets in clean view'],
        icon: <CalendarClock size={24} />,
        gradient: 'from-purple-500 to-violet-600',
        image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&q=80&w=800' // Construction/Architecture
    },
    {
        id: 'reports',
        title: 'Deal Analytics & Reports',
        description: 'Investor-ready insights.',
        bullets: ['Generate ROI breakdowns', 'Compare across projects'],
        icon: <BarChart3 size={24} />,
        gradient: 'from-rose-500 to-pink-600',
        image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800' // Data/Charts
    }
];

// Abstract UI Mock Component for carousel slides
const AbstractFeatureVisual: React.FC<{ feature: FeatureSlide }> = ({ feature }) => (
    <div className={`w-full h-full relative overflow-hidden bg-gradient-to-br ${feature.gradient}`}>
        {/* Render the mapped image with overlay */}
        <img
            src={feature.image}
            alt={feature.title}
            className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

        <div className="absolute inset-0 p-6 flex flex-col justify-between z-10">
            <div className="self-end">
                <div className="size-12 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center text-white border border-white/20 shadow-lg">
                    {feature.icon}
                </div>
            </div>

            {/* Feature label */}
            <div className="text-white">
                <span className="text-lg font-bold font-display shadow-black/10 drop-shadow-md">{feature.title}</span>
            </div>
        </div>
    </div>
);

const UpgradeModal: React.FC<UpgradeModalProps> = ({ open, onClose }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const navigate = useNavigate();

    const handleGetInvestorPro = () => {
        onClose();
        navigate('/marketplace');
    };

    // Prevent body scroll when modal is open (optional but good UX)
    // useEffect(() => {
    //   if (open) document.body.style.overflow = 'hidden';
    //   else document.body.style.overflow = 'unset';
    //   return () => { document.body.style.overflow = 'unset'; };
    // }, [open]);

    if (!open) return null;

    const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % features.length);
    const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + features.length) % features.length);

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal Container */}
            <div className={`relative bg-white md:rounded-3xl shadow-2xl w-full ${uiScale.modalPanel.split(' ')[2]} ${uiScale.modalMaxHeight} flex flex-col overflow-hidden animate-[scaleIn_0.25s_ease-out]`}>

                {/* Fixed Header / Close Button Area (only if needed, otherwise absolute) */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-30 p-2 rounded-xl bg-white/80 backdrop-blur-sm text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all shadow-sm"
                >
                    <X size={20} />
                </button>

                {/* Scrollable Content Wrapper */}
                <div className="flex-1 overflow-y-auto overscroll-contain min-h-0">
                    <div className="flex flex-col lg:flex-row h-full">
                        {/* Left: Carousel (60%) */}
                        <div className={`lg:w-[55%] bg-slate-50 ${uiScale.modalBody} flex flex-col justify-center min-h-[250px] md:min-h-[300px]`}>
                            {/* Carousel Container */}
                            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-xl shadow-slate-200/50 transform transition-all group">
                                <AbstractFeatureVisual key={features[currentSlide].id} feature={features[currentSlide]} />

                                {/* Navigation Arrows */}
                                <div className="absolute inset-0 flex items-center justify-between px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none group-hover:pointer-events-auto">
                                    <button
                                        onClick={(e) => { 
                                            e.preventDefault();
                                            e.stopPropagation(); 
                                            prevSlide(); 
                                        }}
                                        className="size-9 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center text-slate-700 hover:bg-white hover:scale-105 transition-all cursor-pointer pointer-events-auto z-10"
                                        aria-label="Previous slide"
                                    >
                                        <ChevronLeft size={18} />
                                    </button>
                                    <button
                                        onClick={(e) => { 
                                            e.preventDefault();
                                            e.stopPropagation(); 
                                            nextSlide(); 
                                        }}
                                        className="size-9 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center text-slate-700 hover:bg-white hover:scale-105 transition-all cursor-pointer pointer-events-auto z-10"
                                        aria-label="Next slide"
                                    >
                                        <ChevronRight size={18} />
                                    </button>
                                </div>
                            </div>

                            {/* Feature Info */}
                            <div className="mt-6 text-center lg:text-left">
                                <h3 className="text-xl font-bold text-slate-900 font-display mb-2">
                                    {features[currentSlide].title}
                                </h3>
                                <p className="text-slate-500 text-sm mb-4 leading-relaxed">{features[currentSlide].description}</p>

                                {/* Dots Navigation */}
                                <div className="flex items-center justify-center lg:justify-start gap-1.5">
                                    {features.map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentSlide(index)}
                                            className={`h-1.5 rounded-full transition-all duration-300 ${index === currentSlide
                                                ? 'w-6 bg-slate-900'
                                                : 'w-1.5 bg-slate-300 hover:bg-slate-400'
                                                }`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right: Upgrade Pitch (45%) */}
                        <div className={`lg:w-[45%] ${uiScale.modalBody} flex flex-col bg-white`}>
                            {/* Header */}
                            <div className="mb-4 md:mb-6">
                                <div className="inline-flex items-center gap-1.5 px-2 md:px-3 py-0.5 md:py-1 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 rounded-full text-[9px] md:text-[10px] font-bold mb-3 md:mb-4 shadow-sm shadow-amber-100/50">
                                    <Sparkles size={9} className="md:w-[10px] md:h-[10px]" strokeWidth={2.5} />
                                    <span>Investor Pro</span>
                                </div>
                                <h2 className={`${uiScale.titleLg} md:text-2xl lg:text-3xl mb-2 md:mb-3`}>
                                    Unlock the Investment Suite
                                </h2>
                                <p className={`${uiScale.body} text-xs md:text-sm`}>
                                    Advanced tools to track, analyze, and manage your real estate portfolio.
                                </p>
                            </div>

                            {/* Included in Pro - Compact List */}
                            <div className="flex-1 mb-8">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">
                                    Everything in Free, plus:
                                </p>
                                <ul className="space-y-3">
                                    {features.map((feature) => (
                                        <li key={feature.id} className="flex items-start gap-3 text-sm text-slate-600 group">
                                            <div className={`mt-0.5 size-5 rounded bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-white shadow-sm group-hover:scale-110 transition-transform`}>
                                                <Check size={12} strokeWidth={3} />
                                            </div>
                                            <span className="font-medium">{feature.title}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Eligibility */}
                            <div className="bg-amber-50 rounded-lg p-3 mb-4 border border-amber-100">
                                <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-0.5">
                                    Eligibility Criteria
                                </p>
                                <p className="text-xs text-amber-800 leading-relaxed">
                                    Access to the Investment Management Suite is exclusively reserved for active investors. Your account will be granted full access upon securing your first unit via the platform.
                                </p>
                            </div>

                            {/* CTAs - Sticky Bottom on Mobile? No, inside scroll. */}
                            <div className="space-y-3 mt-auto pt-4 border-t border-slate-50">
                                <button
                                    onClick={handleGetInvestorPro}
                                    className="block w-full py-3.5 px-6 bg-slate-900 text-white text-sm font-bold rounded-xl hover:bg-slate-800 hover:shadow-lg hover:shadow-slate-900/20 active:scale-[0.98] transition-all text-center"
                                >
                                    Get Investor Pro
                                </button>
                                <button
                                    onClick={onClose}
                                    className="block w-full py-2.5 px-6 text-slate-500 text-sm font-medium hover:text-slate-900 transition-colors text-center"
                                >
                                    Maybe later
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpgradeModal;
